export interface ToolFaq {
  q: string;
  a: string;
}

export interface ToolDef {
  slug:
    | "fuel-cost-calculator"
    | "mpg-calculator"
    | "trip-cost-calculator"
    | "payload-calculator"
    | "trailer-weight-distribution-calculator"
    | "ev-charging-calculator"
    | "gas-vs-ev-cost-calculator";
  name: string;
  h1: string;
  description: string;
  answer: string;
  faqs?: ToolFaq[];
}

export const TOOLS: ToolDef[] = [
  {
    slug: "fuel-cost-calculator",
    name: "Fuel Cost Calculator",
    h1: "Fuel Cost Calculator",
    description:
      "Work out what any vehicle costs to fuel per year, month and mile from its MPG, your annual mileage and local fuel price.",
    answer:
      "Annual fuel cost = (miles driven per year ÷ MPG) × price per gallon. At 15,000 miles a year, 30 MPG and $3.40 a gallon, that's about $1,700 a year, or $142 a month.",
  },
  {
    slug: "mpg-calculator",
    name: "MPG Calculator",
    h1: "MPG Calculator",
    description:
      "Calculate your real-world fuel economy from trip miles and gallons filled, and see how far it sits from the EPA rating.",
    answer:
      "MPG = miles driven ÷ gallons used. Drive 320 miles and fill 12.4 gallons and you averaged 25.8 MPG. Fill the tank fully at both ends of the measurement for an accurate result.",
  },
  {
    slug: "trip-cost-calculator",
    name: "Trip Cost Calculator",
    h1: "Trip Gas Cost Calculator",
    description:
      "Estimate the fuel cost of a specific drive from distance, fuel economy and gas price, split per person and round trip.",
    answer:
      "Trip fuel cost = (trip miles ÷ MPG) × price per gallon. A 500-mile drive at 28 MPG with $3.40 gas costs about $61 one way, or $121 round trip.",
  },
  {
    slug: "payload-calculator",
    name: "Payload Calculator",
    h1: "Truck & Trailer Payload Calculator",
    description:
      "Find your remaining payload capacity from GVWR and curb weight, then subtract passengers, cargo and tongue weight to see if you're over the limit.",
    answer:
      "Payload capacity = GVWR − curb weight. A truck with a 7,200 lb GVWR and 5,100 lb curb weight has 2,100 lb of payload. Subtract passengers, cargo and any trailer tongue weight from that figure to see what's left.",
    faqs: [
      {
        q: "What's the difference between GVWR and payload?",
        a: "GVWR (Gross Vehicle Weight Rating) is the maximum total weight the vehicle can safely weigh, including itself. Payload is GVWR minus curb weight — the amount you're allowed to add in passengers, cargo and tongue weight.",
      },
      {
        q: "Does tongue weight count against payload?",
        a: "Yes. Tongue weight presses down on the tow vehicle's hitch, so it comes out of your payload capacity just like passengers and cargo do.",
      },
    ],
  },
  {
    slug: "trailer-weight-distribution-calculator",
    name: "Trailer Weight Distribution Calculator",
    h1: "Trailer Weight Distribution Calculator",
    description:
      "Check whether your tongue weight sits in the safe 10-15% (conventional) or 15-25% (fifth wheel) range, and see remaining tow rating headroom.",
    answer:
      "Tongue weight should be 10-15% of trailer gross weight for a conventional hitch, or 15-25% for a fifth wheel or gooseneck. A 7,000 lb trailer needs 700-1,050 lb of tongue weight on a bumper hitch.",
    faqs: [
      {
        q: "What happens if tongue weight is too low?",
        a: "Too little tongue weight (under 10%) lets the trailer sway side to side at speed, which can become dangerous, especially in crosswinds or when being passed by trucks.",
      },
      {
        q: "What happens if tongue weight is too high?",
        a: "Too much tongue weight (over 15% on a conventional hitch) overloads the rear axle and hitch, can exceed your tow vehicle's payload capacity, and causes the front end to feel light and vague to steer.",
      },
    ],
  },
  {
    slug: "ev-charging-calculator",
    name: "EV Charging Calculator",
    h1: "EV Charging Time & Cost Calculator",
    description:
      "Estimate EV charging time and cost from battery size, charge percentage, charger power and electricity price, plus savings vs a gas car.",
    answer:
      "Charging time (hours) ≈ (battery kWh × % to add) ÷ (charger kW × efficiency). Charging a 75 kWh battery from 20% to 80% on an 11.5 kW Level 2 charger takes about 4.3 hours at roughly 90% efficiency.",
    faqs: [
      {
        q: "Why isn't charging 100% efficient?",
        a: "Some energy is lost as heat in the battery, charger and cables during AC-to-DC conversion. Level 2 home charging is typically about 85-90% efficient; DC fast charging is usually a bit higher.",
      },
      {
        q: "Is DC fast charging always the best choice?",
        a: "It's the fastest, but charging speed tapers sharply above about 80% state of charge to protect the battery, and frequent fast charging can accelerate long-term battery wear compared with Level 2 charging.",
      },
    ],
  },
  {
    slug: "gas-vs-ev-cost-calculator",
    name: "Gas vs EV Cost Calculator",
    h1: "Gas vs Electric Car Cost Calculator (5-Year TCO)",
    description:
      "Compare the true cost of owning a gas car versus an EV: purchase price, incentives, fuel or electricity, maintenance, insurance and resale value over your ownership period.",
    answer:
      "Total cost of ownership = purchase price − incentives − resale value + fuel or charging + maintenance + insurance over the years you keep the car. A $35,000 gas car at 30 MPG and 15,000 miles a year typically costs about $1,700 a year to fuel, while an equivalent EV at 3.5 mi/kWh and $0.16/kWh costs about $690 — roughly $5,000 saved over five years before maintenance and incentives.",
    faqs: [
      {
        q: "Are EVs really cheaper to maintain?",
        a: "Generally yes. EVs have no oil changes, spark plugs, timing belts, exhaust or transmission fluid, and regenerative braking makes brake pads last far longer. Tires wear faster because of the extra weight and instant torque, so budget slightly more there.",
      },
      {
        q: "Does the federal EV tax credit apply to my car?",
        a: "The credit depends on the model year, where the vehicle and battery were assembled, the purchase price cap and your income. Enter whatever incentive you actually qualify for in the incentives field rather than assuming the full amount.",
      },
      {
        q: "What about depreciation?",
        a: "Resale value is often the single largest ownership cost, larger than fuel. The calculator subtracts the value you expect to recover when you sell, so a car that holds its value can beat a cheaper car that doesn't.",
      },
      {
        q: "How many miles until an EV breaks even?",
        a: "Divide the extra upfront cost (EV price minus incentives, minus the gas car's price) by the annual savings in fuel and maintenance. Most mainstream EVs break even somewhere between 3 and 7 years of average driving.",
      },
    ],
  },
];

export const TOOL_SLUGS = TOOLS.map((t) => t.slug);
export function getTool(slug: string) {
  return TOOLS.find((t) => t.slug === slug);
}
